/*

  e y e k a n d y  e v e n t  l o g g i n g
  EyekandyEventLogging

  :description:
  A class to handle all of the logging requirements.

*/

//
//  :code:
import { talk, renderMessage, isTruthy } from '../utils'
import { URLS, VERSION } from '../settings'

//
//  :packages:
import { v4 } from 'uuid'

class EyekandyEventLogging {
  /**
   * Constructs our EyekandyEventLogging object.
   * @param {Object} options Requires the keys: 'environment', 'distributor', 'language'.
   */
  constructor (options) {
    this.version = VERSION
    //
    //  Group for logging identifier.
    this.group = `EKY-EVENT-LOGGING`
    this.destination = null // Should be set to the logging output type of 'projects' or 'syndication'.
    //
    //  Basic validation on the options inputs.
    if (!options) {
      throw new Error(
        renderMessage(
          this.group,
          '001',
          `error, options are required to construct this object`
        )
      )
    }
    if (typeof options !== 'object') {
      throw new Error(
        renderMessage(this.group, '002', `error, options must be an object`)
      )
    }
    //
    //  Advanced validation of options.
    this.validateOptions(options)
    //
    //  Now we know the options are valid we can set the attributes of ourselves.
    //  :core:
    this.environment = options.environment
    this.distributor = options.distributor
    this.language = options.language
    this.location = options.location || null
    //
    //  :ids:
    this.session = null
    this.user = null
    this.memory = {
      'eky-session': null,
      'eky-user': null
    }
    this.sessionKey = 'eky-session'
    this.userKey = 'eky-user'
    //
    //  :runtime:
    this.sent = []
    this.pending = []
    this.MEMORY_ONLY_STORAGE_ONLY = options.memoryOnly
    //
    //  Set our interval running for queue processing.
    this.queueDaemon = setInterval(this.processQueue.bind(this), 2000)
    this.isProcessingQueue = false
    //
    //  Set our interval running for resetting the session uuid.
    //  TODO: Improve and save the next refresh time for the session uuid in local storage.
    this.sessionResetDaemon = setInterval(
      this.resetSession.bind(this),
      59 * 60 * 1000
    )
    //
    //  Now run our generic initialisation function.
    this.initialise()
  }

  storageWrapper (type, key, action, value) {
    //
    //  :step 0:
    //  If we've been flagged to not use local storage, don't.
    if (this.MEMORY_ONLY_STORAGE_ONLY === true) {
      return null
    }
    try {
      //
      //  :step 1:
      //  Set our area dynamically.
      let area = null
      //
      //  Note, a DOM error will raise here on access denied (just by calling the variable.)
      if (type === 'session') {
        area = window.sessionStorage
      }
      if (type === 'local') {
        area = window.localStorage
      }
      //
      //  :step 2:
      //  Complete the action as instructed by the user.
      if (action === 'remove') {
        area.removeItem(key)
        return true
      }
      if (action === 'set') {
        area.setItem(key, value)
        return true
      }
      if (action === 'get') {
        return area.getItem(key)
      }
    } catch (err) {
      return null
    }
    return null
  }

  /**
   * A function that is called whenever we lose focus on the current window, we process the logs in the queue at that point.
   * They may not actually be sent right now, as the OS may have suspended the page, but they will send the instant the page
   * is returned to.
   */
  windowNoLongerInFocusCallback () {
    this.processQueue()
  }

  initialise () {
    //
    //  Run our functions to get the values from storage/create them.
    this.getSessionUuid()
    this.getUserUuid()
    //
    //  Add a listener to the window to process the queue on unload
    document.addEventListener(
      'visibilitychange',
      this.windowNoLongerInFocusCallback.bind(this)
    )
    window.addEventListener(
      'onpagehide',
      this.windowNoLongerInFocusCallback.bind(this)
    )
    //talk("log", "EKY-EEL", `startup complete for user '${this.user}' @ session '${this.session}'`)
  }

  resetSession () {
    this.session = v4()
    this.storageWrapper('session', this.sessionKey, 'set', this.session)
  }

  purge () {
    //
    //  Remove the values from local storage.
    this.storageWrapper('session', this.sessionKey, 'remove')
    this.storageWrapper('local', this.userKey, 'remove')
    //
    //  Stop and discard any events that are in the queue.
    this.stopProcessingQueue()
    this.pending = []
  }

  //
  //  :internal:
  //  Functions that operate on our self.
  getLastSentLogPayload () {
    if (this.sent) {
      return JSON.parse(this.sent[this.sent.length - 1].data)
    }
    return null
  }

  getLastSentLogUrl () {
    if (this.sent) {
      return this.sent[this.sent.length - 1].location
    }
    return null
  }

  //
  //  :validation:
  //  Functions to handle validation / checks etc.
  //  -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -

  validateOptions (options) {
    //
    //  Ensure that we have all of the core information in the options.
    const requiredKeys = ['environment', 'distributor', 'language']
    const missingKeys = []
    for (var i = 0; i < requiredKeys.length; i++) {
      if (!isTruthy(options[requiredKeys[i]], true)) {
        missingKeys.push(requiredKeys[i])
      }
    }
    if (missingKeys.length > 0) {
      throw new Error(
        renderMessage(
          this.group,
          '003',
          `error, the following required keys are missing values in options '${JSON.stringify(
            missingKeys
          )}'`
        )
      )
    }
    //
    //  Validate the environment against the enum.
    const validEnvironments = ['dev', 'prod']
    if (validEnvironments.indexOf(options.environment) === -1) {
      throw new Error(
        renderMessage(
          this.group,
          '004',
          `error, environment must be one of '${JSON.stringify(
            validEnvironments
          )}'`
        )
      )
    }
  }

  //
  //  :storage:
  //  Functions that interact with storage.
  //  -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -

  /**
   * Reads our own value for the given key if possible, else reads from the storage area, else creates the value and sets it in
   * the storage area and to our self.
   * @param {sessionStorage or localStorage} area
   * @param {String} key
   * @returns The value for the key at the given storage location.
   */
  //
  //  @Louis: Removed 2022-01-24 - Cannot use the variable name "sessionStorage" or "localStorage" in some contexts.
  /*
  readAndSetUuidValueFromStorage(area, key) {
    //
    //  It is possible to get permission denied here, handle via try/catch.
    try {
      //
      //  If we have the value of that key set to null/falsey at the moment, read/set it from storage.
      if (!isTruthy(this[key])) {
        const areaKey = `eky-${key}`
        //
        //  Read it from storage.
        const storedValue = area.getItem(areaKey)
        if (isTruthy(storedValue)) {
          //
          //  Read the stored value from storage.
          this[key] = storedValue
        } else {
          //
          //  There is no value in storage, add one now.
          this[key] = v4()
          area.setItem(areaKey, this[key])
        }
      }
      return this[key]
    } catch (err) {
      //
      //  If permission was denied, we still need to have some form of values for our UUIDs.
      //  Error, this is creating far too many UUIDs.
      this[key] = v4()
      return this[key]
    }
  }
  */

  _upsertToSelf (key, value) {
    if (key === this.sessionKey) {
      this._upsertSessionToSelf(value)
    }
    if (key === this.userKey) {
      this._upsertUserToSelf(value)
    }
  }
  _upsertSessionToSelf (value) {
    this.session = value
    this.memory[this.sessionKey] = value
  }
  _upsertUserToSelf (value) {
    this.user = value
    this.memory[this.userKey] = value
  }

  _getUUIDFromStorageOrMemory (area, key) {
    //
    //  :step 1:
    //  Maybe there is already a value in storage?
    const storedValue = this.storageWrapper(area, key, 'get')
    //
    //  If there is a value here, return it.
    if (storedValue) {
      this._upsertToSelf(key, storedValue)
      return storedValue
    }

    //
    //  :step 2:
    //  There isn't a value in storage, so create a new one.
    const value = `${v4()}`
    //
    //  This next line sets our UUID in localStorage.
    const wasSet = this.storageWrapper(area, key, 'set', value)
    //
    //  If it was set, great, return it.
    if (wasSet) {
      this._upsertToSelf(key, value)
      return value
    }

    //
    //  :step 3:
    //  We couldn't read/set it from storage, do we already have a memory value?
    if (this.memory[key]) {
      this._upsertToSelf(key, this.memory[key])
      return this.memory[key]
    }

    //
    //  :step 4:
    //  We don't have any stored value here, so save to self and return.
    //  Caution: this value is tied to the persistance of our self.
    this._upsertToSelf(key, value)

    //
    //  All done, we can just return the value to the caller.
    return value
  }

  /**
   * A function to return the current session uuid (may read/create it in storage).
   * @returns {String}
   */
  getSessionUuid () {
    return this._getUUIDFromStorageOrMemory('session', this.sessionKey)
  }

  /**
   * A function to return the current user uuid (may read/create it in storage).
   * @returns {String}
   */
  getUserUuid () {
    return this._getUUIDFromStorageOrMemory('local', this.userKey)
  }

  //
  //  :network:
  //  Functions that interact with the network.
  //  -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -

  _failedToSendViaBeaconCallback (payload) {
    talk('error', '[EKY-EL-100] unable to send via beacon:', payload)
  }

  _readDestinationUrl (level) {
    //
    //  Get our destination URL.
    const urls = URLS[this.environment][this.destination]
    if (!urls) {
      throw new Error(
        renderMessage(
          this.group,
          '400',
          `error, unknown destination. Is the 'destination' set correctly? (Should be either 'projects' ` +
            `or 'syndication'), aborting logging action`
        )
      )
    }
    //
    //  Now read the URL for our level.
    const url = urls[level]
    if (!url) {
      throw new Error(
        renderMessage(
          this.group,
          '401',
          `error, the url to log to is not defined, aborting logging action`
        )
      )
    }
    return url
  }

  _safeSendBeacon (url, data) {
    navigator.sendBeacon(url, data)
  }

  /**
   * Our main function to handle actually sending the logging data out to the world.
   * @param {Object} payload The payload that should be logged, must conform the destination's requirement.
   * @returns {Boolean} True if success, else False. Console error level messages are written when things fail to send.
   */
  _sendViaBeacon (level, payload, send) {
    if (!send) {
      send = false
    }
    //
    //  Read the destination URL.
    const url = this._readDestinationUrl(level)
    //
    //  Serialise our data.
    const data = JSON.stringify(payload, null, 2)
    //
    //  Try to send using the modern way (all except from IE11 can use this).
    try {
      if (navigator) {
        if (navigator.sendBeacon) {
          let success = false
          if (send) {
            //
            //  @Louis: We can't read this response anymore (Jan 2023, CORB).
            this._safeSendBeacon(url, data)
            //
            //  We have to mark this as true, but it may of failed, hopefully it raises an error if it failed.
            success = true
          } else {
            success = this.pushToQueue(level, data)
          }
          if (!success) {
            this._failedToSendViaBeaconCallback(payload)
          }
          //
          //  Store the log we sent locally.
          this.sent.push({ location: url, data: data, success: success })
          //
          //  Return true/false (same as sendBeacon).
          return success
        }
      }
    } catch (err) {
      talk(
        'log',
        this.group,
        `warning, sendBeacon API isn't present in this environment, falling back to XMLHTTPRequest`
      )
    }

    //
    //  If we get to here there is no beacon support, so we'll have to request it via the legacy helper.
    try {
      this._sendViaBeaconLegacy(url, data, payload)
      return true
    } catch (err) {
      return false
    }
  }

  _sendViaBeaconLegacy (url, data, payload) {
    //
    //  @Louis: Moved to fetch as XMLHTTPRequest was having issues.
    let p = payload
    if (!Array.isArray(p)) {
      p = [p]
    }
    let payload_ = JSON.stringify(p)
    //
    //  Don't capture the response here to avoid CORB, just set it running in the background.
    fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'text/plain'
      },
      body: payload_
    })

    //
    //  Store the log locally.
    this.sent.push({ location: url, data: data, success: true })
    return true
  }

  //
  //  :logging:
  //  Functions that actually do the logging or edit payloads.
  //  -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -     =     -
  _addClassAndRuntimeValuesToPayload (payload) {
    //
    //  :class values:
    //  Set our values from our self.
    payload.distributor = this.distributor
    payload.language = this.language
    payload.session = this.session
    payload.user = this.user
    //
    //  :runtime values
    //  Add in the information that will always be sent out with every log.
    payload.location = this.location || window.location.href
    payload.referrer = document.referrer || null
    //
    //  Add our timestamp to the payload, note this is the real time that will be used in the record.
    const now = new Date()
    payload.ts = now.toISOString().replace('T', ' ').replace('Z', '')
    //
    //  :user values:
    //  Now run the callback that the child classes will use to insert their own values.
    //  Caution here the user can easily delete/overwrite our values (intended for now).
    payload = this.preLogCallback(payload)
    //
    //  Return the payload as it will actually be logged.
    return payload
  }

  /**
   * A function that is destined to be defined in child classes, when it is called all of the data from the initial
   * .log() call and the data from this class have already been added to the payload.
   * @param {Object} payload
   * @returns The payload as it will be logged.
   */
  preLogCallback (payload) {
    return payload
  }

  /**
   * Our actual function that triggers sending the log out to the network. Caution, it'll log whatever you pass as payload
   * so it's important to make sure it's valid/correct data by using the .log() function.
   * @param {Object} payload
   * @returns {Boolean} True if it was logged, else False.
   */
  _log (payload) {
    return this._sendViaBeacon(
      'log',
      this._addClassAndRuntimeValuesToPayload(payload)
    )
  }

  /**
   * Our actual function that triggers sending the error out to the network. Caution, it'll log whatever you pass as payload
   * so it's important to make sure it's valid/correct data by using the .error() function.
   * @param {Object} payload
   * @returns {Boolean} True if it was logged, else False.
   */
  _error (payload) {
    return this._sendViaBeacon(
      'error',
      this._addClassAndRuntimeValuesToPayload(payload)
    )
  }

  //
  //  :queue related:
  //  Functions that handle/marshall the queue.
  pushToQueue (level, data) {
    this.pending.push({ level: level, data: data })
    return true
  }

  processQueue () {
    //
    //  Don't process if we're already processing.
    if (this.isProcessingQueue) {
      return false
    }
    //
    //  Take a copy of the queue as it is just now.
    let queueNow = []
    Object.assign(queueNow, this.pending)
    if (queueNow.length === 0) {
      return false
    }
    //
    //  Set our lock to true for the queue.
    this.isProcessingQueue = true
    //
    //  Empty the queue because we've just "locked it" when we copied it above..
    this.pending = []
    //
    //  Group the queue by destination URL -> [events].
    const groupedQueue = {}
    queueNow.map(task => {
      if (typeof groupedQueue[task.level] === 'undefined') {
        groupedQueue[task.level] = []
      }
      groupedQueue[task.level].push(JSON.parse(task.data))
    })
    //
    //  Now process the grouped queue.
    for (const [level, datas] of Object.entries(groupedQueue)) {
      this._sendViaBeacon(level, datas, true)
    }
    //
    //  Return true as we've processed something.
    this.isProcessingQueue = false
    return true
  }

  stopProcessingQueue () {
    if (this.queueDaemon) {
      clearInterval(this.queueDaemon)
      this.queueDaemon = null
    }
  }

  //
  //  :helpers:
  //  Generic logging helper functions to cut down on duplication.
  logClick (serviceOrProject, detail, arid) {
    return this.log('click', detail, serviceOrProject, arid)
  }

  logDelivery (serviceOrProject, detail, arid) {
    return this.log('delivery', detail, serviceOrProject, arid)
  }

  logScan (serviceOrProject, detail, arid) {
    return this.log('scan', detail, serviceOrProject, arid)
  }

  logAddToCart (serviceOrProject, detail, arid) {
    return this.log('add-to-cart', detail, serviceOrProject, arid)
  }

  logOpen (serviceOrProject, detail, arid) {
    return this.log('open', detail, serviceOrProject, arid)
  }

  logClose (serviceOrProject, detail, arid) {
    return this.log('close', detail, serviceOrProject, arid)
  }

  logARStart (serviceOrProject, detail, arid) {
    return this.log('ar-start', detail, serviceOrProject, arid)
  }

  logAREnd (serviceOrProject, detail, arid) {
    return this.log('ar-end', detail, serviceOrProject, arid)
  }
}

//
//  :exports:
export default EyekandyEventLogging
